import { useRouter } from '@/lib/navigation';
import { useTranslations } from '@/i18n';
import TopPanel from '@/components/ui/TopPanel';
import TripForm, { TripFormValues } from '@/components/trips/form/TripForm';
import { Button } from '@/components/ui/button';
import type { TripDetail, TripUser } from '@/hooks/trips/types';

interface TripFormLayoutProps {
  mode: 'create' | 'edit';
  tripId?: string;
  tripNumber?: string;
  initialData?: Partial<TripFormValues>;
  currentUser?: { id: string; email: string; name: string };
  tripAuthor?: TripUser;
  backUrl?: string;
  onSubmit?: (values: TripFormValues) => Promise<void>;
  derivedMode?: 'extend' | 'shorten' | null;
  parentTrip?: TripDetail | null;
  parentTripId?: string | null;
  parentRequestNumber?: string | null;
  /** Pre-loaded acting employee name from trip data (used as fallback before list is fetched) */
  actingEmployeeName?: string;
}

export default function TripFormLayout({
  mode,
  tripId,
  tripNumber,
  initialData,
  currentUser,
  tripAuthor,
  backUrl = '/',
  onSubmit,
  derivedMode = null,
  parentTrip = null,
  parentTripId = null,
  parentRequestNumber = null,
  actingEmployeeName,
}: TripFormLayoutProps) {
  const router = useRouter();
  const t = useTranslations('tripForm');
  const tNav = useTranslations('navigation');

  return (
    <div className="min-h-screen bg-white">
      {/* Fixed TopPanel */}
      <div className="fixed top-0 right-0 left-0 z-50">
        <TopPanel />
      </div>

      <main className="bg-white pt-[5.875rem] pb-24">
        <div className="relative z-20 mx-auto max-w-[76.9375rem] px-4 py-[1.5rem] sm:px-8 sm:py-[2.62rem]">
          {/* Page Title Section */}
          <div className="pb-[2rem]">
            <h1 className="mb-2 text-[2rem] leading-[2.5rem] font-medium text-[#292929]">
              {t('pageTitle')}
            </h1>
            <p className="text-base leading-6 text-[#666666]">
              {t('tripNumber')} -{' '}
              <span className="font-medium text-[#292929]">{tripNumber || t('newTrip')}</span>
            </p>
          </div>

          {/* Form Card */}
          <div className="mx-auto w-full max-w-[42rem]">
            <TripForm
              mode={mode}
              tripId={tripId}
              tripNumber={tripNumber}
              initialData={initialData}
              currentUser={currentUser}
              tripAuthor={tripAuthor}
              onSubmit={onSubmit}
              derivedMode={derivedMode}
              parentTripId={parentTripId}
              parentTrip={parentTrip}
              parentRequestNumber={parentRequestNumber}
              actingEmployeeName={actingEmployeeName}
            />
          </div>
        </div>

        {/* Corner Logo - kept behind page content */}
        <div className="pointer-events-none fixed right-0 bottom-0 z-10">
          <img src="/assets/corner-logo.svg" alt="" className="h-[18.75rem] w-[18.75rem]" />
        </div>

        {/* Fixed Bottom Bar */}
        <div className="fixed right-0 bottom-0 left-0 z-40">
          <div className="border-t border-[#f1f1f1] bg-white">
            {/* Desktop */}
            <div className="hidden items-center justify-between px-12 py-4 lg:flex">
              <Button
                variant="ghost"
                onClick={() => router.push(backUrl)}
                className="flex h-auto cursor-pointer items-center justify-center gap-1.5 rounded-[0.75rem] px-4 py-2 hover:bg-[#f9f9f9]"
              >
                <img src="/assets/icons/chevron-left.svg" alt="" className="h-5 w-5" />
                <span className="text-base font-medium text-black">{tNav('back')}</span>
              </Button>

              <Button
                type="submit"
                form="trip-form"
                variant="ghost"
                className="flex h-11 min-w-[10rem] items-center justify-center gap-2 rounded-full bg-[#f9f9f9] px-6 text-sm font-medium text-[#bbbbbb] hover:bg-[#ebebeb]"
              >
                <span className="whitespace-nowrap">{t('saveAndContinue')}</span>
              </Button>
            </div>

            {/* Mobile */}
            <div className="flex w-full min-w-0 items-center justify-between gap-2 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] lg:hidden">
              <Button
                variant="ghost"
                onClick={() => router.push(backUrl)}
                className="flex h-10 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-black hover:bg-[#f1f1f1]"
              >
                <img src="/assets/icons/chevron-left.svg" alt="" className="h-5 w-5" />
                {tNav('back')}
              </Button>

              <Button
                type="submit"
                form="trip-form"
                variant="ghost"
                className="ml-auto flex h-11 items-center justify-center gap-2 rounded-full bg-[#f9f9f9] px-5 text-sm font-medium text-[#bbbbbb] hover:bg-[#ebebeb]"
              >
                <span className="whitespace-nowrap">{t('saveAndContinue')}</span>
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
